// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import { Navigate, useParams } from 'react-router-dom';
import { useSession, hasAnyRole } from '../context/SessionContext';

interface ProtectedRouteProps {
  /** Required roles to access this route. Admin always has access. */
  requiredRoles?: string[];
  /** If true, allows access when the URL param 'id' matches the user's member_id */
  allowSelf?: boolean;
  /** URL param name to check for self-access (default: 'id') */
  selfParamName?: string;
  /** Where to redirect unauthorized users (default: '/dashboard') */
  redirectTo?: string;
  children: React.ReactNode;
}

/**
 * A route wrapper that checks role-based access.
 *
 * - If requiredRoles is provided, user must have one of those roles (admin always has access)
 * - If allowSelf is true, user can access if the URL param matches their member_id
 * - Unauthorized users are redirected to redirectTo (default: /dashboard)
 */
export function ProtectedRoute({
  requiredRoles,
  allowSelf = false,
  selfParamName = 'id',
  redirectTo = '/dashboard',
  children
}: ProtectedRouteProps) {
  const { session } = useSession();
  const params = useParams();

  // If no role restrictions, allow access
  if (!requiredRoles || requiredRoles.length === 0) {
    return <>{children}</>;
  }

  // Check if user has required role (admin always has access)
  const hasRole = hasAnyRole(session, requiredRoles);

  // Check if accessing own resource
  const isSelf = allowSelf && session?.member_id && params[selfParamName] === session.member_id;

  if (hasRole || isSelf) {
    return <>{children}</>;
  }

  // Unauthorized - redirect to configured destination
  return <Navigate to={redirectTo} replace />;
}

export default ProtectedRoute;
